import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useResumenMes } from '@/hooks/useResumenMes';
import type { FiltroVenta } from '@/lib/filtroVenta';
import { cn } from '@/lib/utils';
import type { SemanaOperativa } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';

/**
 * Resumen del Mes (réplica del cuadro de la app original): por cada semana
 * operativa del MISMO MES que la seleccionada, muestra Crono/Costo esperado vs
 * real (los promedios del reporte semanal), con el total acumulado a la fecha y
 * la diferencia. Los datos vienen de useResumenMes (caché compartida).
 */
export function ResumenMesVista({
  semanas,
  semanaSel,
  filtroVenta,
}: {
  semanas: SemanaOperativa[];
  semanaSel: number | null;
  filtroVenta: FiltroVenta;
}) {
  const { filas, tot, difCrono, difCosto, cargando } = useResumenMes(
    semanas,
    semanaSel,
    filtroVenta,
  );

  return (
    <Card className="overflow-hidden print:break-inside-avoid print:shadow-none">
      <div className="flex items-center gap-2 border-b border-border bg-zinc-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Resumen del mes
        {cargando && (
          <CircleNotch className="h-3 w-3 animate-spin" weight="bold" aria-hidden="true" />
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Semana</TableHead>
            <TableHead>Período</TableHead>
            <TableHead className="text-right">Días Ef.</TableHead>
            <TableHead className="text-right">Crono Esp.</TableHead>
            <TableHead className="text-right">Crono Real</TableHead>
            <TableHead className="text-right">Costo Esp.</TableHead>
            <TableHead className="text-right">Costo Real</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.map((f) => {
            const esSel = Number(f.semana.id) === semanaSel;
            return (
              <TableRow
                key={f.semana.id}
                className={cn(esSel && 'bg-adelante-primary/20 font-medium')}
              >
                <TableCell
                  className={cn(
                    'font-mono text-xs font-semibold',
                    esSel && 'border-l-4 border-adelante-primary text-adelante-primary-dark',
                  )}
                >
                  S{f.semana.numero_semana}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {periodo(f.semana.fecha_inicio)} → {periodo(f.semana.fecha_fin)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-sm">
                  {f.semana.dias_efectivos}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
                  {pct(f.cronoEsp)}
                </TableCell>
                <TableCell
                  className={cn(
                    'text-right font-mono tabular-nums text-sm font-semibold',
                    f.baseSemanal && f.cronoReal >= f.cronoEsp
                      ? 'text-adelante-primary-dark'
                      : f.baseSemanal
                        ? 'text-red-600'
                        : 'text-muted-foreground',
                  )}
                >
                  {f.baseSemanal ? pct(f.cronoReal) : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
                  {pct(f.costoEsp)}
                </TableCell>
                <TableCell
                  className={cn(
                    'text-right font-mono tabular-nums text-sm font-semibold',
                    f.baseSemanal && f.costoReal >= f.costoEsp
                      ? 'text-adelante-primary-dark'
                      : f.baseSemanal
                        ? 'text-red-600'
                        : 'text-muted-foreground',
                  )}
                >
                  {f.baseSemanal ? pct(f.costoReal) : '—'}
                </TableCell>
              </TableRow>
            );
          })}
          {filas.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                Sin semanas en este mes.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow className="border-t-2">
            <TableCell className="font-semibold" colSpan={3}>
              Total a la fecha
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold text-muted-foreground">
              {pct(tot.cronoEsp)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
              {pct(tot.cronoReal)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold text-muted-foreground">
              {pct(tot.costoEsp)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
              {pct(tot.costoReal)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-semibold" colSpan={3}>
              Diferencia (real − esperado)
            </TableCell>
            <TableCell />
            <TableCell
              className={cn(
                'text-right font-mono tabular-nums text-xs font-semibold',
                difCrono < 0 ? 'text-red-600' : 'text-adelante-primary-dark',
              )}
            >
              {difCrono >= 0 ? '+' : ''}
              {pct(difCrono)}
            </TableCell>
            <TableCell />
            <TableCell
              className={cn(
                'text-right font-mono tabular-nums text-xs font-semibold',
                difCosto < 0 ? 'text-red-600' : 'text-adelante-primary-dark',
              )}
            >
              {difCosto >= 0 ? '+' : ''}
              {pct(difCosto)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Card>
  );
}

function pct(n: number): string {
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

/** dd/mm desde un ISO YYYY-MM-DD. */
function periodo(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}`;
}
